import { useState, useCallback, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import {
  Bell,
  Check,
  Copy,
  Mail,
  Paperclip,
  Pencil,
  Trash2,
  User,
} from 'lucide-react'
import { FBPage } from 'components/FbUI/Page/FBPage'
import { EntityDetailLayout } from 'components/EntityDetailPage/EntityDetailLayout'
import { SidebarSection } from 'components/EntityDetailPage/SidebarSection'
import { StageChevronBar } from 'components/SalesStage/StageChevronBar'
import { getStageColor } from 'components/SalesStage/stageColors'
import { EmailsModal } from 'components/NewCompanyPage/Modals/EmailsModal/EmailsModal'
import { ContactCompany } from 'models/companies'
import { DealDetail } from 'models/deals'
import { SalesStage } from 'models/sales_stages'
import useApiService from 'services/api'
import { usdFormatterOrFallback } from 'utils/formatting'
import { ActionsDropdown } from 'components/Buttons/ActionsDropdown'
import { cn } from 'components/UI/cn'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { MULTI_VALUE_SELECT_COLORS } from 'utils/constants'
import { EditDealModal } from 'components/Deals/EditDealModal/EditDealModal'
import { EditDealLocationsModal } from './EditDealLocationsModal'
import { EditDealProductModal } from './EditDealProductModal'
import { DealEventHistory } from './DealEventHistory'
import { NotFoundContent } from '../not-found-content'

const TAGS_COLLAPSED_COUNT = 4

type DealDetailTab = 'activity' | 'notes' | 'reminders' | 'documents'

// Terminal buttons in fixed order: Lost | Churn | Won
const TERMINAL_ORDER = ['closed lost', 'churn', 'closed won'] as const
const TERMINAL_LABELS: Record<string, string> = {
  'closed lost': 'Lost',
  churn: 'Churn',
  'closed won': 'Won',
}

// ─── Deal Detail Page ─────────────────────────────────────────────────────────

export function DealDetailPage() {
  const { dealId } = useParams<{ dealId: string }>()
  const api = useApiService()
  const queryClient = useQueryClient()

  const dealIdNum = dealId ? parseInt(dealId) : undefined
  const { data: deal, isLoading, isError, refetch } = api.useGetDeal(dealIdNum)
  const { data: stages } = api.useGetSalesStages()

  const [activeTab, setActiveTab] = useState<DealDetailTab>('activity')
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showEditDeal, setShowEditDeal] = useState(false)
  const [showEditLocations, setShowEditLocations] = useState(false)
  const [showEditProduct, setShowEditProduct] = useState(false)
  const [tagsExpanded, setTagsExpanded] = useState(false)
  const [hoverCompanies, setHoverCompanies] = useState(false)
  const [hoverProducts, setHoverProducts] = useState(false)
  const [copiedId, setCopiedId] = useState(false)
  const navigate = useNavigate()

  const handleDeleteDeal = useCallback(async () => {
    if (!dealIdNum) return
    if (
      !window.confirm(
        'Are you sure you want to delete this deal? This cannot be undone.'
      )
    )
      return
    try {
      await api.deleteDeal(dealIdNum)
      toast.success('Deal deleted')
      navigate('/deals')
    } catch {
      toast.error('Failed to delete deal')
    }
  }, [dealIdNum, api, navigate])

  const invalidateActivity = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dealActivity', dealIdNum] })
  }, [queryClient, dealIdNum])

  /** Refetch deal data + refresh activity feed after any deal mutation. */
  const handleDealSaved = useCallback(async () => {
    await refetch()
    invalidateActivity()
  }, [refetch, invalidateActivity])

  const handleStageChange = useCallback(
    async (stageId: number) => {
      if (!dealIdNum) return
      try {
        await api.editDeal(dealIdNum, { sales_stage: stageId })
        await handleDealSaved()
      } catch {
        toast.error('Failed to update stage')
      }
    },
    [dealIdNum, api, handleDealSaved]
  )

  const handleCopyId = useCallback(() => {
    if (!deal?.identifier) return
    navigator.clipboard.writeText(deal.identifier).then(() => {
      setCopiedId(true)
      toast.success('Deal ID copied')
      setTimeout(() => setCopiedId(false), 2000)
    })
  }, [deal?.identifier])

  const entityBreadcrumbs = useMemo(
    () => (deal?.identifier ? [{ label: `#${deal.identifier}` }] : []),
    [deal?.identifier]
  )

  if (isLoading) {
    return (
      <FBPage className="gap-y-4" noContainer innerPageClassName="!pb-0 !pr-0">
        <div className="flex items-center justify-center h-64 text-fg-secondary text-sm">
          Loading deal...
        </div>
      </FBPage>
    )
  }

  if (!deal || isError) {
    return <NotFoundContent />
  }

  const allStages = stages ?? []

  const terminalStages = TERMINAL_ORDER.map((name) =>
    allStages.find((s) => s.stage.toLowerCase() === name)
  ).filter(Boolean) as SalesStage[]

  const dealName =
    deal.name ||
    deal.contact_company?.name ||
    `Deal #${deal.identifier ?? deal.id}`

  // "Last updated X days ago" label
  const daysSinceModified = deal.modified
    ? Math.floor(dayjs().diff(dayjs(deal.modified), 'day'))
    : null
  const lastUpdatedText =
    daysSinceModified === null
      ? null
      : daysSinceModified === 0
        ? 'today'
        : daysSinceModified === 1
          ? '1 day ago'
          : `${daysSinceModified} days ago`

  // For EmailsModal: build a minimal ContactCompany shape
  const contactCompanyForModal = deal.contact_company
    ? ({
        id: deal.contact_company.id,
        name: deal.contact_company.name,
      } as unknown as ContactCompany)
    : undefined

  const displayTags = deal.deal_taglist ?? []
  const visibleTags = tagsExpanded
    ? displayTags
    : displayTags.slice(0, TAGS_COLLAPSED_COUNT)

  return (
    <EntityDetailLayout
      entityBreadcrumbs={entityBreadcrumbs}
      scrollMode="page"
      sidebar={
        <div className="w-[300px] flex-shrink-0 flex flex-col">
          {/* Stat cards */}
          <div className="flex gap-3 mb-1">
            <div className="flex-1 bg-surface-alt rounded-lg border border-line shadow-md p-3">
              <p className="text-xxs text-fg-secondary">
                {deal.monthly_revenue_override != null
                  ? '1yr Revenue Override'
                  : '1yr Revenue Forecast'}
              </p>
              <p className="text-base font-semibold text-fg">
                {usdFormatterOrFallback(deal.revenue_display)}
              </p>
            </div>
            <div className="flex-1 bg-surface-alt rounded-lg border border-line shadow-md p-3">
              <p className="text-xxs text-fg-secondary">Est. Close Date</p>
              <p className="text-base font-semibold text-fg">
                {deal.close_date_override
                  ? dayjs(deal.close_date_override).format('MMM D, YYYY')
                  : '-'}
              </p>
            </div>
          </div>

          {/* Summary */}
          <SidebarSection title="Summary" defaultOpen>
            <DealInfoRows deal={deal} stages={allStages} />
          </SidebarSection>

          {/* Companies */}
          <div
            onMouseEnter={() => setHoverCompanies(true)}
            onMouseLeave={() => setHoverCompanies(false)}
          >
            <SidebarSection
              title="Companies"
              defaultOpen
              headerRight={
                <button
                  className={cn(
                    'transition-opacity p-1 rounded hover:bg-surface-hover',
                    hoverCompanies ? 'opacity-100' : 'opacity-0'
                  )}
                  onClick={() => setShowEditLocations(true)}
                >
                  <Pencil size={14} className="text-fg-secondary" />
                </button>
              }
            >
              <DealCompaniesSection
                deal={deal}
                onEditLocations={() => setShowEditLocations(true)}
              />
            </SidebarSection>
          </div>

          {/* Products */}
          {deal.product_detail && (
            <div
              onMouseEnter={() => setHoverProducts(true)}
              onMouseLeave={() => setHoverProducts(false)}
            >
              <SidebarSection
                title="Products"
                defaultOpen
                headerRight={
                  <button
                    className={cn(
                      'transition-opacity p-1 rounded hover:bg-surface-hover',
                      hoverProducts ? 'opacity-100' : 'opacity-0'
                    )}
                    onClick={() => setShowEditProduct(true)}
                  >
                    <Pencil size={14} className="text-fg-secondary" />
                  </button>
                }
              >
                <DealProductsSection product={deal.product_detail} />
              </SidebarSection>
            </div>
          )}

          {/* Tags */}
          <SidebarSection
            title="Tags"
            defaultOpen
            headerRight={
              displayTags.length > TAGS_COLLAPSED_COUNT ? (
                <button
                  className="text-xs text-brand hover:underline"
                  onClick={() => setTagsExpanded((e) => !e)}
                >
                  {tagsExpanded ? 'Hide' : 'View All'}
                </button>
              ) : undefined
            }
          >
            <DealTagsSection tags={visibleTags} />
          </SidebarSection>

          {/* Contacts */}
          <SidebarSection title="Contacts" defaultOpen>
            <DealContactsSection
              contacts={deal.contacts ?? []}
              companyName={deal.contact_company?.name}
            />
          </SidebarSection>
        </div>
      }
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          {deal.identifier && (
            <button
              type="button"
              onClick={handleCopyId}
              className="text-sm text-fg-secondary hover:text-fg flex items-center gap-1 w-fit transition-colors"
            >
              #{deal.identifier}
              {copiedId ? <Check size={12} /> : <Copy size={12} />}
            </button>
          )}
          <h1 className="text-2xl font-semibold text-fg leading-tight">
            {dealName}
          </h1>
          {lastUpdatedText && daysSinceModified !== null && (
            <p
              className={cn(
                'text-sm text-fg-secondary border-b-2 w-fit',
                daysSinceModified < 14
                  ? 'border-success'
                  : daysSinceModified < 30
                    ? 'border-warning'
                    : 'border-error'
              )}
            >
              Last updated {lastUpdatedText}
            </p>
          )}
        </div>
        <ActionsDropdown
          actions={[
            {
              label: 'Edit this deal',
              icon: Pencil,
              onClick: () => setShowEditDeal(true),
            },
            {
              label: 'Schedule a reminder',
              icon: Bell,
              onClick: () => setActiveTab('reminders'),
            },
            {
              label: 'Delete this deal',
              icon: Trash2,
              onClick: handleDeleteDeal,
              variant: 'destructive',
            },
          ]}
          containerWidth={200}
          className="bg-surface rounded-md p-1"
        />
      </div>

      {/* Stage chevron bar + terminal stage buttons */}
      {allStages.length > 0 && (
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <StageChevronBar
              stages={allStages}
              activeStageId={deal.sales_stage}
              onStageChange={handleStageChange}
            />
          </div>
          {terminalStages.length > 0 && (
            <div
              className="flex items-center border border-line rounded-md overflow-hidden flex-shrink-0 bg-surface"
              style={{ height: 34 }}
            >
              {terminalStages.map((stage, i) => {
                const isActive = stage.id === deal.sales_stage
                const stageColor = getStageColor(
                  stage,
                  allStages.indexOf(stage),
                  allStages.length
                )
                return (
                  <button
                    key={stage.id}
                    type="button"
                    onClick={() => handleStageChange(stage.id)}
                    className={cn(
                      'px-3 h-full text-sm transition-colors bg-surface',
                      i > 0 && 'border-l border-line',
                      isActive
                        ? 'font-medium'
                        : 'text-fg-secondary hover:bg-surface-hover'
                    )}
                    style={
                      isActive
                        ? {
                            backgroundColor: stageColor.bg,
                            color: stageColor.text,
                          }
                        : undefined
                    }
                  >
                    {TERMINAL_LABELS[stage.stage.toLowerCase()] ?? stage.stage}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* White content area */}
      <div className="flex flex-col gap-4 flex-1 min-h-0 bg-surface rounded-lg p-4">
        {/* Note composer placeholder (FIR-6274) */}
        <NoteComposerPlaceholder />

        {/* Tab bar + Inbox button on same row */}
        <DealTabBar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          inboxButton={
            contactCompanyForModal ? (
              <button
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-brand text-fg-inverse rounded-md hover:opacity-90 transition-opacity flex-shrink-0"
                onClick={() => setShowEmailModal(true)}
              >
                <Mail size={14} />
                Inbox
                {deal.assigned_thread_count != null &&
                  deal.assigned_thread_count > 0 && (
                    <span className="ml-1 bg-surface text-brand text-xxs rounded-full px-1.5 py-0.5 font-medium">
                      {deal.assigned_thread_count}
                    </span>
                  )}
              </button>
            ) : undefined
          }
        />

        {/* Tab content */}
        <div className="flex-1">
          {activeTab === 'activity' && <DealEventHistory dealId={deal.id} />}
          {activeTab === 'notes' && <DealNotesPlaceholder />}
          {activeTab === 'reminders' && <DealRemindersPlaceholder />}
          {activeTab === 'documents' && <DealDocumentsPlaceholder />}
        </div>
      </div>

      {/* Email Modal — pre-filtered to this deal's threads */}
      {showEmailModal && contactCompanyForModal && (
        <EmailsModal
          show={showEmailModal}
          onHide={() => setShowEmailModal(false)}
          contactCompany={contactCompanyForModal}
        />
      )}

      {/* Edit Locations Modal */}
      {deal.contact_company && (
        <EditDealLocationsModal
          show={showEditLocations}
          onClose={() => setShowEditLocations(false)}
          dealId={deal.id}
          contactCompanyId={deal.contact_company.id}
          companyTotalDoors={deal.contact_company.total_doors}
          currentApplyTo={deal.apply_to}
          onSaved={handleDealSaved}
        />
      )}

      {/* Full Edit Deal Modal (same as company page) */}
      <EditDealModal
        show={showEditDeal}
        onClose={() => {
          setShowEditDeal(false)
          invalidateActivity()
        }}
        dealId={deal.id}
        dealsTableQueryKey={['deal', String(deal.id)]}
      />

      {/* Edit Product Modal */}
      <EditDealProductModal
        show={showEditProduct}
        onClose={() => setShowEditProduct(false)}
        dealId={deal.id}
        currentProductId={deal.product_id}
        currentProductVariantId={deal.product_variant_id}
        onSaved={handleDealSaved}
      />
    </EntityDetailLayout>
  )
}

// ─── Deal Info Rows ───────────────────────────────────────────────────────────

function DealInfoRows({
  deal,
  stages,
}: {
  deal: DealDetail
  stages: SalesStage[]
}) {
  const { preferences } = usePreferences()
  const reportingUnit = preferences?.reporting_unit || 'lbs'

  const stageEntry = stages.find((s) => s.id === deal.sales_stage)
  const stageIndex = stages.findIndex((s) => s.id === deal.sales_stage)
  const stageColor = stageEntry
    ? getStageColor(stageEntry, stageIndex, stages.length)
    : null

  const volumeDisplay =
    deal.volume_display != null
      ? `${deal.volume_display.toLocaleString()} ${reportingUnit.toUpperCase()}`
      : '-'

  return (
    <div className="flex flex-col gap-2 text-sm">
      <InfoRow
        label="Name"
        value={deal.name || deal.contact_company?.name || '-'}
      />
      <InfoRow
        label="ID"
        value={deal.identifier ? `#${deal.identifier}` : '-'}
      />
      {stageEntry && stageColor && (
        <div className="flex items-start justify-between gap-2">
          <span className="text-fg-secondary flex-shrink-0">Stage</span>
          <span
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
            style={{ backgroundColor: stageColor.bg, color: stageColor.text }}
          >
            {stageEntry.stage}
          </span>
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <span className="text-fg-secondary flex-shrink-0">Account Owner</span>
        {deal.account_owner_name ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-line bg-surface-alt text-xs font-medium text-fg">
            <User size={12} className="text-fg-secondary" />
            {deal.account_owner_name}
          </span>
        ) : (
          <span className="text-fg text-right">-</span>
        )}
      </div>
      <InfoRow
        label={
          deal.monthly_volume_override != null
            ? '1yr Volume Override'
            : '1yr Volume Forecast'
        }
        value={volumeDisplay}
      />
      <InfoRow
        label="Date Added"
        value={deal.created ? dayjs(deal.created).format('MMM D, YYYY') : '-'}
      />
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-fg-secondary flex-shrink-0">{label}</span>
      <span className="text-fg text-right">{value}</span>
    </div>
  )
}

// ─── Products Section ─────────────────────────────────────────────────────────

function DealProductsSection({
  product,
}: {
  product: NonNullable<DealDetail['product_detail']>
}) {
  return (
    <div className="flex flex-col gap-1.5 text-sm">
      {product.product_format && (
        <span className="text-fg-secondary">{product.product_format}</span>
      )}
      <div className="flex flex-col gap-1">
        <Link to="/products" className="text-fg-secondary hover:underline">
          {product.name}
        </Link>
        {product.variants.map((v) => (
          <div key={v.id} className="pl-2">
            <span className="text-brand font-medium">{v.name}</span>
            {v.sku && <p className="text-fg-secondary text-xs">SKU: {v.sku}</p>}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Tags Section ─────────────────────────────────────────────────────────────

const defaultTagColor = MULTI_VALUE_SELECT_COLORS[0]

function DealTagsSection({
  tags,
}: {
  tags: Array<{ id: number; tag: string; color: string | null }>
}) {
  if (tags.length === 0) {
    return <p className="text-sm text-fg-secondary">No tags assigned.</p>
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((tag) => {
        const colorEntry = tag.color
          ? MULTI_VALUE_SELECT_COLORS.find((c) => c.background === tag.color)
          : defaultTagColor
        return (
          <span
            key={tag.id}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            style={{
              backgroundColor:
                colorEntry?.background ?? defaultTagColor.background,
              color: colorEntry?.text ?? defaultTagColor.text,
            }}
          >
            {tag.tag}
          </span>
        )
      })}
    </div>
  )
}

// ─── Contacts Section ─────────────────────────────────────────────────────────

function DealContactsSection({
  contacts,
  companyName,
}: {
  contacts: Array<{
    id: number
    first_name: string
    last_name: string
    title: string
    email: string | null
  }>
  companyName?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const displayedContacts = expanded ? contacts : contacts.slice(0, 5)
  const remaining = contacts.length - 5

  if (contacts.length === 0) {
    return <p className="text-sm text-fg-secondary">No contacts assigned.</p>
  }

  return (
    <div className="flex flex-col gap-2.5 text-sm">
      {displayedContacts.map((contact) => {
        const subtitle = contact.title
          ? companyName
            ? `${contact.title} at ${companyName}`
            : contact.title
          : (companyName ?? null)

        return (
          <div key={contact.id}>
            <p className="truncate">
              <span className="font-medium text-fg">
                {contact.first_name} {contact.last_name}
              </span>
              {contact.email && (
                <span className="text-fg-secondary ml-1">
                  &lt;{contact.email}&gt;
                </span>
              )}
            </p>
            {subtitle && (
              <p className="text-xs text-fg-secondary truncate">{subtitle}</p>
            )}
          </div>
        )
      })}
      {!expanded && remaining > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-xs text-fg-secondary hover:underline text-right"
        >
          More (+{remaining})
        </button>
      )}
    </div>
  )
}

// ─── Companies Section ────────────────────────────────────────────────────────

function DealCompaniesSection({
  deal,
  onEditLocations,
}: {
  deal: DealDetail
  onEditLocations: () => void
}) {
  const { contact_company } = deal
  const distributors = contact_company?.distributors ?? []
  const doorsPreview = deal.doors_preview ?? []
  const doorsCount = deal.doors_count ?? 0
  const totalCompanyDoors = contact_company?.total_doors ?? 0
  const remainingDoors = doorsCount - doorsPreview.length
  const companyUrl = contact_company?.url

  return (
    <div className="flex flex-col gap-1.5 text-sm">
      {/* Distributors */}
      {distributors.map((d) => (
        <span key={d.id} className="text-fg">
          {d.name}
        </span>
      ))}

      {/* Contact company with door count */}
      {contact_company && (
        <div className="flex flex-col gap-1">
          {companyUrl ? (
            <Link to={companyUrl} className="text-fg-secondary hover:underline">
              {contact_company.name}
              {totalCompanyDoors > 0 &&
                ` (${doorsCount}/${totalCompanyDoors} locations)`}
            </Link>
          ) : (
            <span className="text-fg-secondary">
              {contact_company.name}
              {totalCompanyDoors > 0 &&
                ` (${doorsCount}/${totalCompanyDoors} locations)`}
            </span>
          )}

          {/* Door list with vertical connector line */}
          {doorsPreview.length > 0 && (
            <div className="ml-1.5 border-l border-line pl-3 flex flex-col gap-1">
              {doorsPreview.map((door) => (
                <div key={door.id}>
                  {companyUrl ? (
                    <Link
                      to={companyUrl}
                      className="text-brand hover:underline font-medium"
                    >
                      {door.name}
                    </Link>
                  ) : (
                    <span className="text-brand font-medium">{door.name}</span>
                  )}
                  {door.address && (
                    <p className="text-fg-secondary text-xs">{door.address}</p>
                  )}
                </div>
              ))}
              {remainingDoors > 0 && (
                <button
                  type="button"
                  onClick={onEditLocations}
                  className="text-fg-secondary hover:underline text-xs self-end"
                >
                  More (+{remainingDoors.toLocaleString()})
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Tab Bar ──────────────────────────────────────────────────────────────────

const DEAL_TABS: { key: DealDetailTab; label: string }[] = [
  { key: 'activity', label: 'Activity' },
  { key: 'notes', label: 'Notes' },
  { key: 'reminders', label: 'Reminders' },
  { key: 'documents', label: 'Documents' },
]

function DealTabBar({
  activeTab,
  onTabChange,
  inboxButton,
}: {
  activeTab: DealDetailTab
  onTabChange: (tab: DealDetailTab) => void
  inboxButton?: React.ReactNode
}) {
  return (
    <div className="flex items-center">
      <div className="flex flex-1">
        {DEAL_TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => onTabChange(key)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === key
                ? 'border-brand text-brand'
                : 'border-transparent text-fg-secondary hover:text-fg hover:border-line'
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {inboxButton && <div className="pb-1">{inboxButton}</div>}
    </div>
  )
}

// ─── Note Composer Placeholder (FIR-6274) ────────────────────────────────────

function NoteComposerPlaceholder() {
  // TODO: FIR-6274 — replace with real DealNote composer
  return (
    <div className="border border-line rounded-lg overflow-hidden">
      <div className="w-full px-3 py-2.5 text-sm text-fg-secondary bg-surface cursor-text min-h-[60px]">
        Type your note here
      </div>
      <div className="flex items-center gap-1 px-2 py-1.5 border-t border-line bg-surface">
        {['B', 'I'].map((fmt) => (
          <button
            key={fmt}
            type="button"
            disabled
            className="w-6 h-6 flex items-center justify-center text-xs font-medium text-fg-secondary rounded hover:bg-surface-alt disabled:opacity-40"
          >
            {fmt}
          </button>
        ))}
        <div className="w-px h-4 bg-line mx-1" />
        <Paperclip size={14} className="text-fg-secondary opacity-40" />
      </div>
    </div>
  )
}

// ─── Tab placeholders (wired up in follow-on tickets) ─────────────────────────

function DealNotesPlaceholder() {
  // TODO: FIR-6274 — DealNote model + note composer
  return (
    <div className="flex items-center justify-center h-48 text-fg-secondary text-sm border border-line-subtle rounded-lg">
      Notes — coming in FIR-6274
    </div>
  )
}

function DealRemindersPlaceholder() {
  return (
    <div className="flex items-center justify-center h-48 text-fg-secondary text-sm border border-line-subtle rounded-lg">
      Reminders — coming in Phase 4 (FIR-6277)
    </div>
  )
}

function DealDocumentsPlaceholder() {
  return (
    <div className="flex items-center justify-center h-48 text-fg-secondary text-sm border border-line-subtle rounded-lg">
      Documents — coming in Phase 4 (FIR-6278)
    </div>
  )
}
